import { Hl7Context, Hl7Message, Hl7Segment } from '@medplum/core';
import { Decimal } from 'decimal.js';
import { convertAmountToUnit } from '../util/calculations/mass-units.js';
// Mirrors the legacy Firebird `ASSECO_MSH_RECORD` procedure so outbound messages stay
// byte-compatible with what the AMMS/ASSECO side already parses. Field-by-field:
// MSH-4 (sending facility), MSH-6 (receiving facility), MSH-8 (security), MSH-13
// (sequence number) and MSH-14 (continuation pointer) are always blank in the legacy
// procedure, and MSH-15/16 (accept/application ack type) are hardcoded to 'AL'.
// HL7 standard delimiters: field '|', component '^', repetition '~', escape '\', subcomponent '&'.
// These match Hl7Context's own defaults, so `new Hl7Context()` below is equivalent to passing them explicitly.
const ENCODING_CHARACTERS = '^~\\&';
// Fixed for the AMMS integration: MSH-3 and MSH-5 as observed on outbound traffic to AMMS,
// MSH-12/MSH-18 as required by AMMS for messages sent to it.
const SENDING_APPLICATION = 'ECH';
const RECEIVING_APPLICATION = 'SZPM';
const HL7_VERSION = '2.8';
const CHARACTER_SET = 'WIN1252';
const ACK_TYPE = 'AL';
export const MESSAGE_TYPES = {
    PRESCRIPTION_CREATE_MESSAGE_TYPE: 'MFN^M01',
    PRESCRIPTION_VALIDATE_MESSAGE_TYPE: 'ORM^O01',
    REST_VIAL_DISCARD_MESSAGE_TYPE: 'ZRM^O01',
};
function pad(value) {
    return value.toString().padStart(2, '0');
}
function formatAmmsDateTime(date) {
    return (date.getFullYear().toString() +
        pad(date.getMonth() + 1) +
        pad(date.getDate()) +
        pad(date.getHours()) +
        pad(date.getMinutes()) +
        pad(date.getSeconds()));
}
/** Date-only 'YYYYMMDD' format, e.g. for batch expiry dates. */
export function formatAmmsDate(date) {
    // A batch expiry is a calendar day, read as UTC midnight, so the day is taken in UTC (CY-371).
    return date.getUTCFullYear().toString() + pad(date.getUTCMonth() + 1) + pad(date.getUTCDate());
}
/** Builds MSH-10, e.g. `CYPM0108124652` for order type 'M' and sequence 108124652. */
export function buildAmmsMessageControlId(sequence, orderType = '') {
    return `CYP${orderType}${String(sequence).padStart(10, '0')}`;
}
/**
 * Cypro unit display name → AMMS unit code, for units whose Cypro name differs from what AMMS
 * expects. AMMS rejects a product whose unit it can't resolve (ORA-01400 on GMSL_TOW.JM_ROZCH),
 * so add an entry here once Asseco confirms the AMMS code for a unit. Unlisted units are sent as-is.
 */
const AMMS_UNIT_CODES = {};
/**
 * An outbound message that must not be sent because its content is incomplete (spec §12: "Do not
 * send incomplete message; notify interface operator"). Logged as a FAILED row instead.
 */
export class AmmsMessageValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AmmsMessageValidationError';
    }
}
/** Thrown when an outbound message would carry a blank unit of measure, which AMMS can't import. */
export class AmmsUnitMissingError extends AmmsMessageValidationError {
    constructor(context) {
        super(`Cannot build AMMS message: ${context} has no unit of measure.`);
        this.name = 'AmmsUnitMissingError';
    }
}
/** Maps a Cypro unit to the AMMS unit code; throws `AmmsUnitMissingError` when it is blank. */
export function toAmmsUnit(unit, context = 'a segment') {
    const trimmed = unit?.trim() ?? '';
    if (!trimmed)
        throw new AmmsUnitMissingError(context);
    return AMMS_UNIT_CODES[trimmed] ?? trimmed;
}
export function buildAmmsMshSegment(options) {
    const { messageType, sequence, processingId, countryCode, principalLanguage, orderType, sentAt = new Date() } = options;
    const context = new Hl7Context();
    const messageControlId = buildAmmsMessageControlId(sequence, orderType);
    return new Hl7Segment([
        'MSH',
        ENCODING_CHARACTERS,
        SENDING_APPLICATION,
        '', // MSH-4: sending facility, always blank in the legacy procedure
        RECEIVING_APPLICATION,
        '', // MSH-6: receiving facility, always blank in the legacy procedure
        formatAmmsDateTime(sentAt),
        '', // MSH-8: security, always blank in the legacy procedure
        messageType,
        messageControlId,
        processingId,
        HL7_VERSION,
        '', // MSH-13: sequence number, always blank in the legacy procedure
        '', // MSH-14: continuation pointer, always blank in the legacy procedure
        ACK_TYPE,
        ACK_TYPE,
        countryCode,
        CHARACTER_SET,
        principalLanguage,
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
// Mirrors the legacy Firebird `ASSECO_MFI_RECORD` procedure: MFI-1 (master file identifier)
// is hardcoded to 'ZOK', MFI-2 (master file application identifier) is always blank, and
// MFI-3 (file-level event code) is hardcoded to 'UPD'.
const MASTER_FILE_IDENTIFIER = 'ZOK';
const FILE_LEVEL_EVENT_CODE = 'UPD';
export function buildAmmsMfiSegment(context) {
    return new Hl7Segment([
        'MFI',
        MASTER_FILE_IDENTIFIER,
        '', // MFI-2: master file application identifier, always blank in the legacy procedure
        FILE_LEVEL_EVENT_CODE,
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
function mapChangeTypeToRecordLevelEventCode(changeType) {
    if (changeType === 0 || changeType === 4)
        return 'MAD';
    if (changeType === 1 || changeType === 8)
        return 'MUP';
    return 'MDL';
}
/** Legacy change types passed to `ASSECO_MFE_RECORD_2_10` for a prescription MFN. */
export const AMMS_MFN_CHANGE_TYPES = { add: 0, update: 1, delete: 2 };
export function buildAmmsMfeSegment({ prescriptionId, changeType, context }) {
    const recordLevelEventCode = mapChangeTypeToRecordLevelEventCode(changeType);
    const primaryKeyValue = `${prescriptionId}${context.componentSeparator}${SENDING_APPLICATION}`;
    return new Hl7Segment([
        'MFE',
        recordLevelEventCode,
        '', // MFE-2: MFN control ID, always blank in the legacy procedure
        '', // MFE-3: effective date/time, always blank in the legacy procedure
        primaryKeyValue,
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
// Mirrors the legacy Firebird `ASSECO_ZDR_RECORDS` / `ASSECO_ZDR_WIRKSTOFF_RECORDS`
// procedures. Both build the same 30-field layout; only ZDR-1 (commercial name), ZDR-3
// (dose), ZDR-4 (RQD measurement unit), ZDR-5 (medicine form) and ZDR-6 (package info)
// are ever actually populated by either legacy procedure — everything else (ZDR-2,
// ZDR-11 manufacturer name, ZDR-17 ATC code, ZDR-18 EAN-13 code, ZDR-21 administration
// route, and the unlabeled "blank" slots) is dead placeholder in the source too, so it's
// hardcoded blank here. ZDR-15 (quantity of active ingredient) always ends up equal to
// the dose in `ASSECO_ZDR_RECORDS`, because the legacy procedure only ever seeds it with
// an empty string before falling back to DOSE. ZDR-30 (included in formulary) is
// hardcoded to '0' in both procedures. Unlike MSH/MFI/MFE, neither legacy procedure adds
// a trailing field separator after the last field.
const INCLUDED_IN_FORMULARY = '0';
function buildZdrSegment(context, { commercialName, dose = '', measurementUnit = '', medicineForm = '', packageInfo = '', quantityActiveIngredient = '', }) {
    return new Hl7Segment([
        'ZDR',
        commercialName,
        '', // ZDR-2: international name, always blank in the legacy procedure
        dose,
        measurementUnit,
        medicineForm,
        packageInfo,
        '', // ZDR-7
        '', // ZDR-8
        '', // ZDR-9
        '', // ZDR-10
        '', // ZDR-11: manufacturer name, always blank in the legacy procedure
        '', // ZDR-12
        '', // ZDR-13
        '', // ZDR-14
        quantityActiveIngredient,
        '', // ZDR-16
        '', // ZDR-17: ATC code, always blank in the legacy procedure
        '', // ZDR-18: EAN-13 code, always blank in the legacy procedure
        '', // ZDR-19
        '', // ZDR-20
        '', // ZDR-21: administration route, always blank in the legacy procedure
        '', // ZDR-22
        '', // ZDR-23
        '', // ZDR-24
        '', // ZDR-25
        '', // ZDR-26
        '', // ZDR-27
        '', // ZDR-28
        '', // ZDR-29
        INCLUDED_IN_FORMULARY,
    ], context);
}
/** Builds the ZDR segment for a prescription, mirroring `ASSECO_ZDR_RECORDS`. */
export function buildAmmsZdrSegment(options) {
    const { context, commercialName, doseAmount, measurementUnit, medicineForm } = options;
    const doseUnit = toAmmsUnit(options.doseUnit ?? 'mg', `ZDR dose for ${commercialName}`);
    // Legacy hardcodes a literal '^' here, independent of the message's own delimiters.
    const dose = `${doseAmount}^${doseUnit}^`;
    return buildZdrSegment(context, {
        commercialName,
        dose,
        measurementUnit: toAmmsUnit(measurementUnit, `ZDR-4 for ${commercialName}`),
        medicineForm,
        // Legacy always falls back to this, since PACKAGE_INFO starts blank and is never independently populated.
        packageInfo: `1${context.componentSeparator}${doseUnit}`,
        // Legacy always falls back to this, since QUANTITY_ACTIVE_INGREDIENT starts blank and is never independently populated.
        quantityActiveIngredient: dose,
    });
}
/** Builds the ZDR segment for a bare active ingredient, mirroring `ASSECO_ZDR_WIRKSTOFF_RECORDS`. */
export function buildAmmsZdrWirkstoffSegment(options) {
    return buildZdrSegment(options.context, { commercialName: options.commercialName });
}
/**
 * Derives the AMMS prescription id used in RQD-3 from a therapy sequence's `internalId`
 * (built by `nextPrescriptionInternalId` as `PR-{YYYY}{MM}{DD}-{prescriptionNumber}`), as
 * `{YY}{MM}{DD}{prescriptionNumber}` — a 2-digit year, unlike the internalId's own 4-digit year.
 */
export function deriveAmmsPrescriptionId(internalId) {
    const [, date = '', prescriptionNumber = ''] = internalId.split('-');
    return `${date.slice(2)}${prescriptionNumber}`;
}
function formatAmmsQuantity(value) {
    return value.toFixed(2);
}
// Legacy formats rest-vial quantities (RQD-5/RQD-20 on the ZRM message) with 4 decimals, unlike
// the 2-decimal prescription RQD.
function formatAmmsQuantity4(value) {
    return value.toFixed(4);
}
// Legacy formats the RQD-3 description with a thousands separator, unlike the plain RQD-5/RQD-20 quantity fields.
const AMMS_QUANTITY_WITH_THOUSANDS_FORMAT = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
function formatAmmsQuantityWithThousands(value) {
    return AMMS_QUANTITY_WITH_THOUSANDS_FORMAT.format(value);
}
// RQD-21 (calculation basis): every accepted legacy message carries this literal, regardless of
// the medicine's dose calculation method (spec §8.6/§9.8, Appendix C).
const AMMS_CALCULATION_BASIS = '-1^xxx^ECH';
/** Builds the RQD segment for a prescription's pharmacist-validated dosing, mirroring the legacy AMMS layout. */
export function buildAmmsRqdSegment(options) {
    const { context, prescriptionId, drugName, doseAmount, route, durationMinutes } = options;
    const sub = context.subcomponentSeparator;
    const sep = context.componentSeparator;
    const doseUnit = toAmmsUnit(options.doseUnit ?? 'mg', `RQD-6 for ${drugName}`);
    const quantity = formatAmmsQuantity(doseAmount);
    const description = `${drugName} ${formatAmmsQuantityWithThousands(doseAmount)}${doseUnit}`;
    return new Hl7Segment([
        'RQD',
        '1', // RQD-1: requisition line number, always '1' for a single-line requisition
        '', // RQD-2: item code - internal, not used
        `${prescriptionId}${sub}ECH${sep}${description}`, // RQD-3: item code - external
        '', // RQD-4: item code - manufacturer's, not used
        quantity, // RQD-5: requisition quantity
        doseUnit, // RQD-6: requisition unit of measure
        '', // RQD-7
        '', // RQD-8
        '', // RQD-9
        '', // RQD-10
        '', // RQD-11
        '', // RQD-12
        '', // RQD-13
        '', // RQD-14
        '', // RQD-15
        '', // RQD-16
        '', // RQD-17
        '', // RQD-18
        '', // RQD-19
        quantity, // RQD-20: Asseco secondary quantity, defaults to RQD-5
        AMMS_CALCULATION_BASIS, // RQD-21: calculation basis
        '', // RQD-22
        route, // RQD-23: administration route/form
        durationMinutes != null ? String(durationMinutes) : '', // RQD-24: duration, in minutes
    ], context);
}
/** Builds the OBR segment accompanying RQD, mirroring the legacy AMMS layout. */
export function buildAmmsObrSegment(options) {
    const { context, prescriptionId, logCount } = options;
    const sep = context.componentSeparator;
    return new Hl7Segment([
        'OBR',
        '', // OBR-1: set id, not used
        '', // OBR-2: placer order number, not used
        `${prescriptionId.padStart(10, '0')}${sep}${logCount}${sep}ECH`, // OBR-3: filler order number
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
/**
 * Builds the fixed `~HIS&~ECH&<username>~ECH^<firstName>^<lastName>^...^<username>` composite
 * used for ORC provider fields (e.g. prescribing doctor, validating pharmacist, discarding
 * user). Only the username/first/last name vary — the 'HIS'/'ECH' tokens and
 * repetition/component layout are fixed legacy boilerplate.
 */
export function buildAmmsOrcProviderComposite(context, provider) {
    const { username, firstName, lastName } = provider;
    const rep = context.repetitionSeparator;
    const sub = context.subcomponentSeparator;
    const comp = context.componentSeparator;
    const nameComponents = ['ECH', firstName, lastName, ...Array(11).fill(''), username];
    return ['', `HIS${sub}`, `ECH${sub}${username}`, nameComponents.join(comp)].join(rep);
}
/**
 * ORC-1/ORC-5 pair per workflow step: 'validated' is the pharmacist validation (NW / SC_2,
 * legacy change type 4), 'produced' is production complete (OE / CM, legacy change types 8/9).
 */
const AMMS_ORDER_STATUSES = {
    validated: { orderControl: 'NW', orderStatus: 'SC_2' },
    produced: { orderControl: 'OE', orderStatus: 'CM' },
};
/** Builds the ORC segment for a validated or produced prescription, mirroring the legacy AMMS layout. */
export function buildAmmsOrcSegment(options) {
    const { context, step, prescriptionId, plannedAdministrationAt, generatedAt, doctor, pharmacist, destinationCode, createdAt, } = options;
    const sep = context.componentSeparator;
    const paddedPrescriptionId = prescriptionId.padStart(10, '0');
    const { orderControl, orderStatus } = AMMS_ORDER_STATUSES[step];
    return new Hl7Segment([
        'ORC',
        orderControl, // ORC-1: NW (validated) or OE (produced)
        '', // ORC-2: placer order number, not used
        `${paddedPrescriptionId}${sep}${paddedPrescriptionId}${sep}ECH`, // ORC-3: stable order identifier
        '', // ORC-4: placer group number, not used
        orderStatus, // ORC-5: SC_2 (pharmacist validated) or CM (production complete)
        '', // ORC-6: response flag, not used
        `${sep}${sep}${sep}${formatAmmsDateTime(plannedAdministrationAt)}`, // ORC-7: planned administration time
        '', // ORC-8: parent order, not used
        formatAmmsDateTime(generatedAt), // ORC-9: message generation time
        buildAmmsOrcProviderComposite(context, doctor), // ORC-10: prescribing doctor
        '', // ORC-11: not used
        buildAmmsOrcProviderComposite(context, pharmacist), // ORC-12: validating pharmacist
        // ORC-13: source warehouse — hardcoded legacy value, not sourced from domain data.
        `199${context.repetitionSeparator}HIS${context.subcomponentSeparator}${context.repetitionSeparator}ECH`,
        '', // ORC-14: not used
        '', // ORC-15: not used
        '', // ORC-16: not used
        '', // ORC-17: not used
        '', // ORC-18: not used
        '', // ORC-19: not used
        // ORC-20: destination ward/section, code varies by station; 'HIS'/'ECH' suffix is fixed legacy boilerplate.
        `${destinationCode}${context.repetitionSeparator}HIS${context.subcomponentSeparator}${context.repetitionSeparator}ECH`,
        '', // ORC-21: not used
        '', // ORC-22: not used
        '', // ORC-23: not used
        '', // ORC-24: not used
        '', // ORC-25: not used
        '', // ORC-26: not used
        '', // ORC-27: not used
        '', // ORC-28: not used
        '', // ORC-29: not used
        formatAmmsDateTime(createdAt), // ORC-30: prescription creation timestamp
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
/**
 * Builds the ORC segment for a rest vial disposal (ZRM^O01), mirroring the legacy AMMS layout.
 * ORC-10 (user performing the discard) is left blank, as in the accepted legacy messages.
 */
export function buildAmmsZrmOrcSegment(options) {
    const { context, restVialIdentifier, logCount, transactionAt } = options;
    const sep = context.componentSeparator;
    return new Hl7Segment([
        'ORC',
        'NW', // ORC-1: new/current order notification
        '', // ORC-2: placer order number, not used
        `${restVialIdentifier}${sep}${logCount}${sep}ECH`, // ORC-3: rest vial identifier
        '', // ORC-4: placer group number, not used
        'SC_CM', // ORC-5: pharmacist-validated prescription status
        '', // ORC-6: response flag, not used
        '', // ORC-7: not used
        '', // ORC-8: not used
        formatAmmsDateTime(transactionAt), // ORC-9: transaction timestamp
        '', // ORC-10: user performing discard, blank in the legacy messages
    ], context);
}
/** Builds the NTE segment for optional disposal comments, mirroring the legacy AMMS layout. */
export function buildAmmsNteSegment(options) {
    const { context, comment } = options;
    return new Hl7Segment([
        'NTE',
        '1', // NTE-1: set id
        '', // NTE-2: source, not used
        comment ?? '', // NTE-3: comment
        '', // NTE-4: not used
    ], context);
}
const AMMS_PRODUCT_NOT_SENT = 'NOT SENT FROM AMMS';
/** Builds the RQD segment for a rest vial disposal, mirroring the legacy AMMS layout. */
export function buildAmmsRestVialRqdSegment(options) {
    const { context, restVialNumber, product, quantityDiscarded, unit, packageQuantity } = options;
    const sep = context.componentSeparator;
    // RQD-3: id^product_description^batch_number^expiry_date (spec §10.4).
    const itemCode = product?.ammsProductId
        ? [product.ammsProductId, product.description, product.manufacturerBatch, product.expiryDate].join(sep)
        : AMMS_PRODUCT_NOT_SENT;
    return new Hl7Segment([
        'RQD',
        String(restVialNumber), // RQD-1: rest vial identifier
        '', // RQD-2: item code - internal, not used
        itemCode, // RQD-3: original AMMS stock item
        '', // RQD-4: item code - manufacturer's, not used
        formatAmmsQuantity4(quantityDiscarded), // RQD-5: quantity discarded
        toAmmsUnit(unit), // RQD-6: unit of measure
        '', // RQD-7
        '', // RQD-8
        '', // RQD-9
        '', // RQD-10
        '', // RQD-11
        '', // RQD-12
        '', // RQD-13
        '', // RQD-14
        '', // RQD-15
        '', // RQD-16
        '', // RQD-17
        '', // RQD-18
        '', // RQD-19
        formatAmmsQuantity4(packageQuantity), // RQD-20: original package quantity
        '', // RQD-21
        '', // RQD-22
        '', // RQD-23
        '', // RQD-24
    ], context);
}
/** Builds a single numeric OBX observation segment (weight/height/BSA), mirroring the legacy AMMS layout. */
export function buildAmmsObxSegment(options) {
    const { context, code, name, value, unit } = options;
    const sep = context.componentSeparator;
    return new Hl7Segment([
        'OBX',
        '', // OBX-1: set id, not used
        'NM', // OBX-2: value type, numeric
        `${code}${sep}${name}${sep}ECH`, // OBX-3: observation identifier
        '', // OBX-4: observation sub-id, not used
        formatAmmsQuantity(value), // OBX-5: observation value
        unit, // OBX-6: unit
    ], context);
}
/**
 * Builds the ZIV segment identifying the manufactured preparation, mirroring
 * `ASSECO_ZIV_RECORDS`. Only ZIV-1 is populated; every other field is blank legacy boilerplate.
 */
export function buildAmmsZivFinishedPreparationSegment(options) {
    const { context, productionNumber = '' } = options;
    const sep = context.componentSeparator;
    return new Hl7Segment([
        'ZIV',
        `${productionNumber.replaceAll('-', '')}${sep}ECH`, // ZIV-1: production identifier
        `${sep}${sep}${sep}`, // ZIV-2: blank, 4 components (legacy layout)
        `${sep}${sep}`, // ZIV-3: blank, 3 components (legacy layout)
        `${sep}${sep}${sep}${sep}`, // ZIV-4: blank, 5 components (legacy layout)
        '', // ZIV-5
        '', // ZIV-6
        '', // ZIV-7
        '', // ZIV-8
        `${sep}`, // ZIV-9: blank, 2 components (legacy layout)
        '', // ZIV-10
        '', // ZIV-11
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
/**
 * Builds a reduced ZIV segment for a consumed source batch, mirroring the legacy fallback in
 * `ASSECO_ZIV_RECORDS` for when the batch's original AMMS ZIV text isn't stored. Prefer replaying
 * `batches.rawZiv` verbatim (via `Hl7Segment.parse`) when it's available.
 */
export function buildAmmsZivConsumedBatchSegment(options) {
    const { context, manufacturerBatch, pznOrEan } = options;
    const sep = context.componentSeparator;
    return new Hl7Segment([
        'ZIV',
        `${manufacturerBatch}${sep}ECH`, // ZIV-1: manufacturer batch
        '', // ZIV-2
        `F${sep}faktura${sep}ECH`, // ZIV-3: fixed legacy 'invoice' marker
        '', // ZIV-4
        '', // ZIV-5
        '', // ZIV-6
        '', // ZIV-7
        '', // ZIV-8
        '', // ZIV-9
        pznOrEan, // ZIV-10: PZN/EAN
        '', // trailing empty field to reproduce the legacy procedure's trailing separator
    ], context);
}
/** Builds a ZCM consumption-line segment, mirroring the legacy AMMS layout (spec §9.5/§9.8). */
export function buildAmmsZcmSegment(options) {
    const { context, positionNumber, pznOrEan, tradeName, manufacturerBatch, expiryDate, quantityUsed, packageQuantity, isFirstLine, totalPrescribedAmount, } = options;
    const sep = context.componentSeparator;
    const unit = toAmmsUnit(options.unit, `ZCM-4 for ${tradeName}`);
    return new Hl7Segment([
        'ZCM',
        String(positionNumber), // ZCM-1
        `${pznOrEan}${sep}${tradeName}${sep}${manufacturerBatch}${sep}${expiryDate}`, // ZCM-2
        // ZCM-3: quantity used — legacy emits 2 decimals on the first line and 4 on the following ones
        isFirstLine ? formatAmmsQuantity(quantityUsed) : formatAmmsQuantity4(quantityUsed),
        unit, // ZCM-4: unit of measure
        formatAmmsQuantity(packageQuantity), // ZCM-5: package quantity
        // ZCM-6: total prescribed amount (plain number, unlike ZCM-3/5/7), this component's first line only
        `${isFirstLine ? totalPrescribedAmount : 0}${sep}${unit}`,
        formatAmmsQuantity4(quantityUsed), // ZCM-7: quantity to bill, equals the line consumption
        '0', // ZCM-8: quantity lost
    ], context);
}
// Mirrors the legacy Firebird `ASSECO_PID_RECORD` procedure (spec §8.5, example §9.8): PID-1 is
// blank, PID-2 carries the national identifier (PESEL) and PID-3 the AMMS patient id, PID-5 is
// `family^given`, PID-8 the sex, PID-11 the address and PID-13 the telephone repetitions. Every
// other field — including birth date, marital status, nationality and death date — is blank in
// the accepted legacy messages, which run to PID-32.
const AMMS_SEX_CODES = {
    male: 'M',
    female: 'F',
};
const PID_FIELD_COUNT = 32;
// HL7 v2 escape sequences, so free text can't break the segment's delimiters.
function escapeAmmsText(value, context) {
    return value
        .replaceAll(context.escapeCharacter, '\\E\\')
        .replaceAll(context.fieldSeparator, '\\F\\')
        .replaceAll(context.componentSeparator, '\\S\\')
        .replaceAll(context.subcomponentSeparator, '\\T\\')
        .replaceAll(context.repetitionSeparator, '\\R\\');
}
/** Builds the outbound PID segment for a patient, mirroring `ASSECO_PID_RECORD`. */
export function buildAmmsPidSegment(options) {
    const { context, patient, contact } = options;
    const sep = context.componentSeparator;
    const escape = (value) => escapeAmmsText(value ?? '', context);
    const address = contact?.address;
    const addressField = address && (address.addressLine || address.city || address.postalCode)
        ? [address.addressLine, address.city, address.postalCode].map(escape).join(sep)
        : '';
    // Telephone repetitions; legacy always emits the separator, e.g. `696558381~`.
    const phoneField = contact ? `${escape(contact.phone)}${context.repetitionSeparator}${escape(contact.businessPhone)}` : '';
    const fields = Array(PID_FIELD_COUNT + 1).fill('');
    fields[0] = 'PID';
    fields[2] = patient.nationalId ?? ''; // PID-2: national identifier (PESEL)
    fields[3] = patient.hospitalPatientId ?? ''; // PID-3: AMMS patient id
    fields[5] = `${escape(patient.familyName)}${sep}${escape(patient.givenName)}`; // PID-5: family^given
    fields[8] = patient.sex ? AMMS_SEX_CODES[patient.sex] : ''; // PID-8
    fields[11] = addressField; // PID-11: street^city^postal code
    fields[13] = phoneField; // PID-13: telephone repetitions
    return new Hl7Segment(fields, context);
}
// PV1-2 code, mirrors the reverse of `AmmsPatientClassMap` (server/src/parser/segment-definition.ts).
// 'observation' has no AMMS-side counterpart, so it's left unmapped (blank field).
const AMMS_PATIENT_CLASS_CODES = {
    inpatient: 'I',
    outpatient: 'O',
    emergency: 'E',
};
/**
 * Builds the PV1 segment for a patient encounter, following the AMMS-specific field mapping
 * (`AmmsVisitMapping` in server/src/parser/segment-definition.ts). Only used when the encounter
 * has no `rawPv1` to replay verbatim.
 */
export function buildAmmsPv1Segment(options) {
    const { context, encounter } = options;
    const { patientClass, pointOfCare, room, bed, locationDescription, visitNumber, startedAt, endedAt } = encounter;
    const sep = context.componentSeparator;
    const location = `${pointOfCare ?? ''}${sep}${room ?? ''}${sep}${bed ?? ''}${sep}${sep}${sep}${sep}${sep}${sep}${locationDescription ?? ''}`;
    return new Hl7Segment([
        'PV1',
        '1', // PV1-1: set ID, always '1' for a single-encounter message
        patientClass ? (AMMS_PATIENT_CLASS_CODES[patientClass] ?? '') : '', // PV1-2
        location, // PV1-3: pointOfCare^room^bed^^^^^^locationDescription
        '', // PV1-4: admission type, not used
        '', // PV1-5
        '', // PV1-6
        '', // PV1-7
        '', // PV1-8
        '', // PV1-9
        '', // PV1-10
        '', // PV1-11
        '', // PV1-12
        '', // PV1-13
        '', // PV1-14
        '', // PV1-15
        '', // PV1-16
        '', // PV1-17
        '', // PV1-18
        visitNumber, // PV1-19
        '', // PV1-20
        '', // PV1-21
        '', // PV1-22
        '', // PV1-23
        '', // PV1-24
        '', // PV1-25
        '', // PV1-26
        '', // PV1-27
        '', // PV1-28
        '', // PV1-29
        '', // PV1-30
        '', // PV1-31
        '', // PV1-32
        '', // PV1-33
        '', // PV1-34
        '', // PV1-35
        '', // PV1-36
        '', // PV1-37
        '', // PV1-38
        '', // PV1-39
        '', // PV1-40
        '', // PV1-41
        '', // PV1-42
        '', // PV1-43
        startedAt ? formatAmmsDateTime(new Date(startedAt)) : '', // PV1-44: admitted at
        endedAt ? formatAmmsDateTime(new Date(endedAt)) : '', // PV1-45: discharged at
    ], context);
}
export function createAmmsMessage(options) {
    const { segments, ...mshOptions } = options;
    const msh = buildAmmsMshSegment(mshOptions);
    return new Hl7Message([msh, ...segments], msh.context);
}
/** MSH values shared by every outbound Cypro → AMMS message (`CYPM…` control IDs, PL locale). */
export const AMMS_MSH_DEFAULTS = {
    orderType: 'M',
    processingId: 'P',
    countryCode: 'PL',
    principalLanguage: 'PL',
};
/** MSH-10 of a built message, stored as `hl7_event_log.message_control_id` for ACK correlation. */
export function getAmmsMessageControlId(message) {
    return message.header.getField(10).toString();
}
/** Reason logged on a SKIPPED production report whose batch did not come from AMMS. */
export const AMMS_SKIP_REASON_BATCH_NOT_FROM_AMMS = 'Batch not received from AMMS';
/**
 * Why a production-complete report must not be sent, or null to send it. AMMS can only match
 * batches it sent, so any active-substance batch without a stored raw ZIV (or with no batch record
 * at all) skips the whole report. Carriers and solvents are never passed in.
 */
export function getAmmsProductionSkipReason(batches) {
    return batches.some(batch => !batch?.rawZiv) ? AMMS_SKIP_REASON_BATCH_NOT_FROM_AMMS : null;
}
/** The 'EAN: …' / 'PZN: …' product key used in ZCM-2 and ZIV-10, or undefined when the trade name has neither. */
export function formatAmmsPznOrEan(tradeName) {
    if (tradeName?.barcodeGtin)
        return `EAN: ${tradeName.barcodeGtin}`;
    if (tradeName?.nationalId)
        return `PZN: ${tradeName.nationalId}`;
    return undefined;
}
/**
 * Quantity contained in one commercial package of a trade name, in its strength unit (ZCM-5,
 * ZRM RQD-20). strengthValue is already the total per package for solids and liquids alike (a
 * liquid's concentration is strengthValue / volumeValue). Undefined when the trade name has no
 * strength recorded.
 */
export function computeAmmsPackageContent(tradeName) {
    return tradeName?.strengthValue ?? undefined;
}
/**
 * Consumption lines for a medicine, one per vial drawn (spec §9.7: lines are never consolidated
 * by batch). Taken from the cleanroom DRUG weighings (one per vial, carrying its batch and
 * allocated part of the dose) when any of them knows its batch; otherwise the prescribed dose is
 * filled across the picked vials in picking order, each giving at most its strength, and vials the
 * dose never reaches are left out. Throws AmmsMessageValidationError rather than guess when a
 * vial's strength can't be expressed in the dose unit.
 */
export function computeAmmsConsumptionLines(params) {
    const { batchUsages, weighings, doseUnit, totalPrescribedAmount } = params;
    const pickedBatchIds = new Set(batchUsages.map(usage => usage.batchId));
    const weighedLines = weighings.flatMap(weighing => weighing.step === 'DRUG' && weighing.batchId && weighing.allocatedDose != null && pickedBatchIds.has(weighing.batchId)
        ? [{ batchId: weighing.batchId, quantityUsed: weighing.allocatedDose }]
        : []);
    if (weighedLines.length > 0)
        return weighedLines.filter(line => line.quantityUsed > 0);
    const lines = [];
    let remaining = new Decimal(totalPrescribedAmount);
    for (const usage of batchUsages) {
        if (usage.strength.value == null) {
            throw new AmmsMessageValidationError(`Cannot report AMMS consumption: batch ${usage.batchId} has no strength.`);
        }
        let vialStrength;
        try {
            vialStrength = convertAmountToUnit({ amount: usage.strength.value, from: usage.strength.unit, to: doseUnit });
        }
        catch (error) {
            throw new AmmsMessageValidationError(`Cannot report AMMS consumption for batch ${usage.batchId}: ${error instanceof Error ? error.message : String(error)}`);
        }
        for (let vial = 0; vial < usage.quantity && remaining.gt(0); vial++) {
            const drawn = Decimal.min(vialStrength, remaining);
            lines.push({ batchId: usage.batchId, quantityUsed: drawn.toNumber() });
            remaining = remaining.minus(drawn);
        }
    }
    return lines;
}
/**
 * Why a rest vial disposal must not be reported, or null to send it (spec §10.6/§10.7): AMMS can
 * only write off stock it sent, so a rest vial whose batch has no stored raw ZIV, or no batch
 * record at all, is logged as SKIPPED instead.
 */
export function getAmmsRestVialSkipReason(batch) {
    return getAmmsProductionSkipReason([batch ?? undefined]);
}
/**
 * Builds the ZRM^O01 rest vial disposal ("Extraordinary Losses") message, MSH/ORC/NTE/RQD/ZIV
 * (spec §10). The ZIV replays the batch's original ORR ZIV when stored, else rebuilds the reduced
 * legacy form; without either it's left out.
 */
export function buildAmmsRestVialDiscardMessage(data) {
    const context = new Hl7Context();
    const { batch } = data;
    const zivSegment = batch?.rawZiv
        ? Hl7Segment.parse(batch.rawZiv, context)
        : batch?.manufacturerBatch && batch.pznOrEan
            ? buildAmmsZivConsumedBatchSegment({ context, manufacturerBatch: batch.manufacturerBatch, pznOrEan: batch.pznOrEan })
            : undefined;
    const paddedRestVialNumber = String(data.restVialNumber).padStart(10, '0');
    return createAmmsMessage({
        ...AMMS_MSH_DEFAULTS,
        messageType: MESSAGE_TYPES.REST_VIAL_DISCARD_MESSAGE_TYPE,
        sequence: data.controlSequence,
        sentAt: data.discardedAt,
        segments: [
            buildAmmsZrmOrcSegment({
                context,
                restVialIdentifier: `${data.stockLocationCode}_${paddedRestVialNumber}`,
                logCount: data.logCount,
                transactionAt: data.discardedAt,
            }),
            buildAmmsNteSegment({ context }),
            buildAmmsRestVialRqdSegment({
                context,
                restVialNumber: data.restVialNumber,
                product: data.product,
                quantityDiscarded: data.remainingAmount,
                unit: data.unit,
                packageQuantity: data.packageQuantity,
            }),
            ...(zivSegment ? [zivSegment] : []),
        ],
    });
}
//# sourceMappingURL=message.js.map